import React, { useState, useEffect, useRef } from 'react'
import { Box } from 'grommet'
import { v4 as uuidv4 } from 'uuid'
import * as utils from './utils'
import Avatar from './Avatar'
import Bubble from './Bubble'
import MessageText from './MessageText'
import Composer from './Composer'
import Day from './Day'
import InputToolbar from './InputToolbar'
import LoadEarlier from './LoadEarlier'
import Message from './Message'
import MessageContainer from './MessageContainer'
import Send from './Send'
import Time from './Time'
import GiftedAvatar from './GiftedAvatar'

const GiftedChat = ({
  messages = [],
  onSend = () => {},
  loadEarlier = false,
  onLoadEarlier = () => {},
  keyboardShouldPersistTaps = 'always',
  user = {},
  bottomOffset = 0,
  isLoadingEarlier = false,
  messageIdGenerator = () => uuidv4(),
  textInputProps = {},
  ...props
}) => {
  const [currentMessages, setCurrentMessages] = useState([])

  const inputToolbarRef = useRef(null)

  const [text, setText] = useState('')

  useEffect(() => {
    initMessages(messages)
  }, [messages])

  const initMessages = (messages = []) => {
    setCurrentMessages(messages)
  }

  const renderMessages = () => {
    return (
      <Box style={{ flex: 1 }}>
        <MessageContainer
          {...props}
          messages={currentMessages}
          onSend={onSend}
          loadEarlier={loadEarlier}
          onLoadEarlier={onLoadEarlier}
          keyboardShouldPersistTaps={keyboardShouldPersistTaps}
          user={user}
          bottomOffset={bottomOffset}
          isLoadingEarlier={isLoadingEarlier}
          messageIdGenerator={messageIdGenerator}
          textInputProps={textInputProps}
        />
        {renderChatFooterElement()}
      </Box>
    )
  }

  const handleSend = (messages = [], shouldResetInputToolbar = false) => {
    if (!Array.isArray(messages)) {
      messages = [messages]
    }

    messages = messages.map(message => {
      return {
        ...message,
        user: user,
        createdAt: new Date(),
        _id: messageIdGenerator()
      }
    })

    if (shouldResetInputToolbar === true) {
      resetInputToolbar()
    }
    onSend(messages)
  }

  const resetInputToolbar = () => {
    inputToolbarRef.current.resetValue()
    setText('')
  }

  const handleInputTextChanged = text => {
    if (props.onInputTextChanged) {
      props.onInputTextChanged(text)
    }
    setText(text)
  }

  const renderInputToolbarElement = () => {
    const inputToolbarProps = {
      ...props,
      messages,
      loadEarlier,
      onLoadEarlier,
      keyboardShouldPersistTaps,
      user,
      bottomOffset,
      isLoadingEarlier,
      messageIdGenerator,
      text: text,
      onSend: handleSend,
      onTextChanged: handleInputTextChanged,
      textInputProps: {
        ...textInputProps,
        maxLength: props.maxInputLength,
        initialChatText: props.initialChatText
      }
    }
    if (props.renderInputToolbar) {
      return props.renderInputToolbar(inputToolbarProps)
    }
    return <InputToolbar {...inputToolbarProps} ref={inputToolbarRef} />
  }

  const renderChatFooterElement = () => {
    if (props.renderChatFooter) {
      const footerProps = {
        ...props,
        messages,
        onSend,
        loadEarlier,
        onLoadEarlier,
        keyboardShouldPersistTaps,
        user,
        bottomOffset,
        isLoadingEarlier,
        messageIdGenerator,
        textInputProps
      }
      return props.renderChatFooter(footerProps)
    }
    return null
  }

  return (
    <Box style={styles.container}>
      {renderMessages()}
      {renderInputToolbarElement()}
    </Box>
  )
}

const styles = {
  container: {
    flex: 1
  }
}

export {
  GiftedChat,
  Avatar,
  Bubble,
  MessageText,
  Composer,
  Day,
  InputToolbar,
  LoadEarlier,
  Message,
  MessageContainer,
  Send,
  Time,
  GiftedAvatar,
  utils
}
